import { z } from 'zod';

/**
 * The citation contract. This is the load-bearing type of the whole product: every
 * claim the system makes must resolve to a page a human can open and verify.
 *
 * Invariant enforced server-side before any response leaves the process, and mirrored
 * as a CHECK constraint in the database: `quote` must be a literal substring of the
 * stored page text. A claim whose quote is not in the source is a bug, not a nuance.
 */

/** How a fact was pulled off the page. Drives how much the UI trusts the bbox. */
export const EXTRACTION_METHODS = ['pdf_text', 'vision', 'vision_verified', 'human'] as const;
export type ExtractionMethod = (typeof EXTRACTION_METHODS)[number];

/** Normalized [x0, y0, x1, y1] in 0..1 of the page, origin top-left. DPI-independent. */
export const bboxSchema = z
  .tuple([z.number(), z.number(), z.number(), z.number()])
  .refine(([x0, y0, x1, y1]) => x0 >= 0 && y0 >= 0 && x1 <= 1 && y1 <= 1 && x0 < x1 && y0 < y1, {
    message: 'bbox must be normalized 0..1 with x0<x1 and y0<y1',
  });
export type Bbox = z.infer<typeof bboxSchema>;

export interface CitationRef {
  readonly id: number;
  readonly lenderName: string;
  readonly documentTitle: string;
  /** Null when the document carries no effective date we could parse. */
  readonly documentEffectiveDate: string | null;
  /** True when the document is older than the staleness threshold for its kind. */
  readonly isStale: boolean;
  /** Physical PDF page, 1-based — what the viewer needs. */
  readonly page: number;
  /**
   * The page number *printed on the page*, which frequently differs from the physical
   * index. A broker opening the PDF looks for this one, so both must be carried.
   */
  readonly pageLabel: string | null;
  /** e.g. "3.4.2 LIVING RENT-FREE". Null for matrix pages, which have no sections. */
  readonly section: string | null;
  /** Verbatim source text. Must be a literal substring of the page text. */
  readonly quote: string;
  readonly method: ExtractionMethod;
  readonly confidence: number | null;
  /** Renders the page image with the bbox highlighted. */
  readonly viewerUrl: string;
  /** Opens the original PDF at the page. */
  readonly pdfUrl: string;
  readonly bbox: Bbox | null;
}

/**
 * Marker syntax the model emits inline instead of writing page numbers into prose:
 * "…are ineligible, but three carve-outs apply [[c:412]]."
 *
 * The model never writes a page number itself. It cannot get a citation wrong that it
 * does not author — it can only reference an id that was handed to it.
 */
export const CITATION_MARKER = /\[\[c:(\d+)\]\]/g;

export function extractCitationIds(text: string): number[] {
  const out: number[] = [];
  for (const m of text.matchAll(CITATION_MARKER)) {
    const raw = m[1];
    if (raw !== undefined) out.push(Number(raw));
  }
  return out;
}

/** Strips markers for contexts that render plain text (exports, emails, evals). */
export const stripCitationMarkers = (text: string): string =>
  text.replace(CITATION_MARKER, '').replace(/\s+([.,;:])/g, '$1').replace(/ {2,}/g, ' ').trim();
